import { Dispatch, useContext } from 'react';
import { LoginAction } from './useLoginStatus';
import { ERROR_NO_CLIENT_HTTP_CODE, ERROR_OTP_HTTP_CODE } from '../../rest/responseCodes';
import { LoginOTP } from '../../rest/LoginOTP';
import { LoginResponse2 } from '../../rest/LoginResponse2';
import { LOGIN_NO_CLIENT, SESSION_KEY } from '../../constants/data';
import { LoginResponse1 } from '../../rest/LoginResponse1';
import { useTurnstile } from 'react-turnstile';
import { TurnstileContext } from '../../config/TurnstileProvider';
import useAxios from 'axios-hooks';
import { AxiosResponse } from 'axios';
import { LoginRequestData } from '../../rest/LoginRequestData';
import { UrlsBack } from '../../constants/urls';

const AXIOS_CONFIG = { manual: true };

export const useLoginRequest = (
  tokenInputVisible: boolean,
  dispatch: Dispatch<LoginAction>,
  setRequestErrorMessage: Dispatch<string | undefined>,
  clearRequestErrorMessage: () => void,
  getEmail: () => string | undefined,
  otpInput: string | undefined,
  loginToken: string | undefined,
  setLoginToken: (tkn?: string, expDate?: string) => void,
  afterLogin?: () => void
) => {
  const turnstileConfig = useContext(TurnstileContext);
  const turnstile = useTurnstile();

  const [, post1] = useAxios<LoginResponse1, LoginRequestData>({ url: UrlsBack.LogInPath1, method: 'POST' }, AXIOS_CONFIG);
  const [, post2] = useAxios<LoginResponse2, LoginOTP>({ url: UrlsBack.LogInPath2, method: 'POST' }, AXIOS_CONFIG);

  function getOTPData(): LoginOTP {
    return {
      otp: otpInput ? Number.parseInt(otpInput) : undefined,
    };
  }

  function onError(reason: any) {
    const errorCode = reason?.status;
    let errorResponse = reason;

    if (errorCode === ERROR_OTP_HTTP_CODE && tokenInputVisible) {
      dispatch(LoginAction.ERROR_TOKEN);
    } else if (errorCode === ERROR_NO_CLIENT_HTTP_CODE) {
      dispatch(LoginAction.ERROR);

      /**Se sobreescribe el mensaje de error que proviene
       * del backend con uno especifico para login.*/
      if (errorResponse?.response?.data) {
        errorResponse = {
          ...errorResponse,
          response: {
            ...errorResponse.response,
            data: {
              ...errorResponse.response.data,
              title: LOGIN_NO_CLIENT,
            },
          },
        };
      }
    } else {
      dispatch(LoginAction.ERROR);
    }

    setRequestErrorMessage(errorResponse);
  }

  function processResponse1({ data }: AxiosResponse<LoginResponse1>) {
    if (data?.token && data.token.length > 0) {
      setLoginToken(data.token, undefined);
      clearRequestErrorMessage();
      dispatch(LoginAction.CONTINUAR);
    } else {
      onError(undefined);
    }
  }

  function processResponse2({ data }: AxiosResponse<LoginResponse2>) {
    if (data?.customer?.firstname && data?.customer?.lastname) {
      localStorage.setItem('customerName', `${data.customer.firstname.toUpperCase()} ${data.customer.lastname.toUpperCase()}`);
    }
    if (data?.token && data.token.length > 0) {
      localStorage.setItem(SESSION_KEY, data.token);
      afterLogin?.();
    } else {
      onError(undefined);
    }
  }

  async function request1() {
    localStorage.removeItem(SESSION_KEY);

    if (turnstile == null) {
      onError(undefined);
      return;
    }

    /** Reset() es necesario para obtener un token nuevo
     * (sino devuelve el último).
     */
    turnstile.reset(turnstileConfig?.widgetId);

    turnstile.execute(turnstileConfig?.widgetId, {
      callback: async (token: string) => {
        const requestPayload = {
          email: getEmail(),
          googleToken: token ?? '',
        };

        post1({ data: requestPayload }).then(processResponse1).catch(onError);
      },
      'error-callback': () => {
        onError(undefined);
      },
    });
  }

  async function request2() {
    const tokenHeader = {
      token: loginToken,
    };

    post2({ data: getOTPData(), headers: tokenHeader }).then(processResponse2).catch(onError);
  }

  return [request1, request2] as const;
};
